<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="paymentTableData"
      :columns="paymentTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: [],
          right: [],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-global-filter="currentSavedState?.globalFilter || ''"
      :initial-sorting="currentSavedState?.sorting || [{ id: 'payment_number', desc: false }]"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchPayments"
      :initial-row-selection="initialRowSelection"
      :get-row-id="row => row.id.toString()"
    >
      <template #config-manager>
        <div class="tw-flex tw-flex-col tw-gap-2 tw-text-sm tw-font-bold tw-text-secondary-600">
          {{ t('payments.sepa.select_payments') }}
        </div>
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePaymentStore } from '@/stores/payment.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
} from '@/components/shared/fancyTables';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';
import type { RowSelectionState } from '@tanstack/vue-table';
import PaymentAmountToUseCell from './PaymentAmountToUseCell.vue';
import PaymentReferenceCell from './PaymentReferenceCell.vue';
import OrderNumbers from './OrderNumbers.vue';

const fancyTableRef = ref<any>(null);
const { GetFieldRenderer } = useTableRenderers();
const { getPayments } = usePaymentStore();
const { t } = useI18n();

const initialRowSelection = ref<RowSelectionState>({});

const props = defineProps({
  index: {
    default: 0,
    type: Number,
  },
  customer: {
    default: null,
    type: Number,
  },
});

const emit = defineEmits(['select-payments']);

const isLoading = ref(false);
const paymentTableData = ref<any[]>([]);
const selectedPayments = ref<any[]>([]);
const selectedRowsCache = ref<Map<string, any>>(new Map());
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'payment_number':
        if (value) params.payment_number = value;
        break;
      case 'supplier_name':
        if (value) params.supplier_name = value;
        break;
      case 'invoice_number':
        if (value) params.invoice_number = value;
        break;
      case 'internal_order_number':
        if (value) params.internal_order_number = value;
        break;
      case 'amount':
        if (value?.start !== undefined) params.amount_min = value.start;
        if (value?.end !== undefined) params.amount_max = value.end;
        break;
      case 'date':
        if (value?.from) params.date_after = value.from;
        if (value?.to) params.date_before = value.to;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'payment_number':
      backendField = 'payment_number_without_prefix';
      break;
    case 'supplier_name':
      backendField = 'supplier_name_sort';
      break;
    case 'amount':
      backendField = 'amount_sort';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchPayments = async (options?: ServerTableOptions) => {
  if (!props.customer) return;

  isLoading.value = true;
  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    const filterParams = mapFiltersToApiParams(filters);
    const orderingParam = mapSortingToApiParam(sorting);

    const response = await getPayments({
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customer,
      actual_status: 'APPROVED',
      attached_to_sepa: false,
      is_active: true,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    });

    paymentTableData.value = response.results.map((payment: any) => ({
      ...payment,
      amount_to_use: 'gross_amount',
      invoice_number: payment.invoice_number || '',
    }));
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching payments:', error);
    paymentTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

// Initialize the table config composable
const { currentSavedState, debouncedHandleTableStateChange, isStateLoaded } = useFancyTableConfig({
  tableName: 'select_payments_sepa',
  fancyTableRef,
  tableLoader: async () => {
    await fetchPayments({
      pageIndex: 0,
      pageSize: currentSavedState.value?.pageSize || 10,
      sorting: currentSavedState.value?.sorting || [],
      filters: currentSavedState.value?.columnFilters || [],
    });
  },
});

const paymentTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('payments.titles.payment_no'),
    accessorKey: 'payment_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.LINK_INTERNAL,
    cell: GetFieldRenderer(FieldType.LINK_INTERNAL, {
      link: {
        name: 'payment',
        key: 'id',
      },
    }),
    size: 150,
  },
  {
    header: t('payments.titles.gross_amount'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    size: 150,
  },
  {
    header: t('payments.titles.gross_amount_after_discount'),
    accessorKey: 'gross_eur_less_discount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    size: 200,
  },
  {
    header: t('payments.titles.amount_to_use'),
    accessorKey: 'amount_to_use',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) =>
      h(PaymentAmountToUseCell, {
        key: `amount-to-use-${info.row.original.id}`,
        payment: info.row.original,
        onAmountToUseChanged: handleAmountToUseChange,
      }),
    size: 200,
  },
  {
    header: t('payment.titles.invoice_no'),
    accessorKey: 'invoice_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('payment.titles.internal_order_no'),
    accessorKey: 'internal_order_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('payments.titles.submission_date'),
    accessorKey: 'date',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('suppliers.titles.supplier_name'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.supplier_number'),
    accessorKey: 'supplier_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.released_by'),
    accessorKey: 'released_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: info => {
      const payment = info.row.original;
      return payment.released_by?.display_name || 'N/A';
    },
    size: 150,
  },
  {
    header: t('payment.titles.references'),
    accessorKey: 'invoice_number',
    id: 'reference',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) =>
      h(PaymentReferenceCell, {
        key: `reference-${info.row.original.id}`,
        payment: info.row.original,
        onReferenceChanged: handleReferenceChange,
      }),
    size: 200,
  },
  {
    header: t('orders.titles.orders'),
    accessorKey: 'orders',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: info => h(OrderNumbers, { info }),
    size: 250,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(paymentTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Emit all cached selections
  selectedPayments.value = Array.from(selectedRowsCache.value.values());
  emit('select-payments', selectedPayments.value);
};

const handleAmountToUseChange = (paymentId: number, amountToUse: string) => {
  const paymentIndex = paymentTableData.value.findIndex((p: any) => p.id === paymentId);
  if (paymentIndex !== -1) {
    paymentTableData.value[paymentIndex] = {
      ...paymentTableData.value[paymentIndex],
      amount_to_use: amountToUse,
    };

    // Update in cache too
    const cacheKey = String(paymentId);
    if (selectedRowsCache.value.has(cacheKey)) {
      selectedRowsCache.value.set(cacheKey, {
        ...selectedRowsCache.value.get(cacheKey),
        amount_to_use: amountToUse,
      });
      emit('select-payments', Array.from(selectedRowsCache.value.values()));
    }
  }
};

const handleReferenceChange = (paymentId: number, reference: string) => {
  const paymentIndex = paymentTableData.value.findIndex((p: any) => p.id === paymentId);
  if (paymentIndex !== -1) {
    paymentTableData.value[paymentIndex] = {
      ...paymentTableData.value[paymentIndex],
      invoice_number: reference,
    };

    // Update in cache too
    const cacheKey = String(paymentId);
    if (selectedRowsCache.value.has(cacheKey)) {
      selectedRowsCache.value.set(cacheKey, {
        ...selectedRowsCache.value.get(cacheKey),
        invoice_number: reference,
      });
      emit('select-payments', Array.from(selectedRowsCache.value.values()));
    }
  }
};

watch(
  () => props.customer,
  async newCustomer => {
    if (newCustomer) {
      selectedRowsCache.value.clear();
      selectedPayments.value = [];
      await fetchPayments({
        pageIndex: 0,
        pageSize: pageSize.value,
        sorting: fancyTableRef.value?.getSerializedState()?.sorting || [],
        filters: fancyTableRef.value?.getSerializedState()?.columnFilters || [],
      });
    }
  }
);
</script>
